import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Check, MessageCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../../auth/context/AuthContext';

const PLACEHOLDER_NAME = 'D2D Customer';
const ROUTE_LABELS = {
  CN_GH: 'China to Ghana',
  TR_GH: 'Turkey to Ghana',
  BOTH: 'Both routes',
};

const QUESTIONS = [
  {
    key: 'full_name',
    type: 'text',
    prompt: 'Let us start with your name. What should I call you?',
    placeholder: 'Type your full name',
  },
  {
    key: 'delivery_address',
    type: 'textarea',
    prompt: 'What Ghana pickup or delivery address should we keep on file for you?',
    placeholder: 'Enter your address in Ghana',
  },
  {
    key: 'preferred_routes',
    type: 'single',
    prompt: 'Which shipping route do you want to use most often?',
    options: [
      { value: 'CN_GH', label: 'China to Ghana' },
      { value: 'TR_GH', label: 'Turkey to Ghana' },
      { value: 'BOTH', label: 'Both routes' },
    ],
  },
];

function getAnswers(profile) {
  const state = profile?.onboarding_state || {};
  const fullName = profile?.full_name && profile.full_name !== PLACEHOLDER_NAME ? profile.full_name : '';
  return {
    full_name: fullName,
    delivery_address: state.delivery_address || '',
    preferred_routes: state.preferred_routes || '',
  };
}

function isAnswered(question, answers) {
  const value = answers[question.key];
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(String(value || '').trim());
}

function formatAnswer(question, value) {
  if (question.key === 'preferred_routes') {
    return ROUTE_LABELS[value] || value;
  }

  return value;
}

export default function WelcomeAssistant() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const [profile, setProfile] = useState(user || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [typedPrompt, setTypedPrompt] = useState('');
  const [draftInput, setDraftInput] = useState('');
  const [draftSelections, setDraftSelections] = useState([]);

  useEffect(() => {
    axios.get('/api/customer/onboarding')
      .then((response) => setProfile(response.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!profile && user) {
      setProfile(user);
    }
  }, [profile, user]);

  const answers = useMemo(() => getAnswers(profile || user), [profile, user]);
  const currentQuestionIndex = useMemo(
    () => QUESTIONS.findIndex((question) => !isAnswered(question, answers)),
    [answers]
  );
  const currentQuestion = currentQuestionIndex === -1 ? null : QUESTIONS[currentQuestionIndex];

  useEffect(() => {
    if (!currentQuestion) {
      setTypedPrompt('');
      return undefined;
    }

    setTypedPrompt('');
    const prompt = currentQuestion.prompt;
    let index = 0;
    let intervalId = null;

    const startTimer = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        index += 1;
        setTypedPrompt(prompt.slice(0, index));
        if (index >= prompt.length) {
          window.clearInterval(intervalId);
        }
      }, 18);
    }, 250);

    return () => {
      window.clearTimeout(startTimer);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [currentQuestion?.key]);

  useEffect(() => {
    if (!currentQuestion) {
      setDraftInput('');
      setDraftSelections([]);
      return;
    }

    if (currentQuestion.type === 'multi') {
      setDraftSelections(Array.isArray(answers[currentQuestion.key]) ? answers[currentQuestion.key] : []);
      setDraftInput('');
      return;
    }

    setDraftInput(typeof answers[currentQuestion.key] === 'string' ? answers[currentQuestion.key] : '');
    setDraftSelections([]);
  }, [currentQuestion?.key, answers]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [typedPrompt, answers, loading]);

  const messages = useMemo(() => {
    const conversation = [
      {
        role: 'assistant',
        text: `Hi, I'm Smaiz, your D2D setup assistant. Your account is ready and your warehouse addresses are already live. I just need three quick details so I can finish setting things up for you.`,
      },
      {
        role: 'assistant-card',
      },
    ];

    QUESTIONS.forEach((question, index) => {
      if (currentQuestionIndex !== -1 && index > currentQuestionIndex) return;

      const answer = answers[question.key];
      const answered = isAnswered(question, answers);
      const isCurrent = currentQuestion && question.key === currentQuestion.key;

      if (answered) {
        conversation.push({ role: 'assistant', text: question.prompt });
        conversation.push({ role: 'user', text: formatAnswer(question, answer) });
      } else if (isCurrent) {
        conversation.push({
          role: 'assistant',
          text: typedPrompt || '',
          typing: typedPrompt.length < question.prompt.length,
        });
      }
    });

    if (profile?.onboarding_completed || currentQuestionIndex === -1) {
      conversation.push({
        role: 'assistant',
        text: "Everything is set. I'm taking you to your dashboard now.",
      });
    }

    return conversation;
  }, [answers, currentQuestion, currentQuestionIndex, profile?.onboarding_completed, typedPrompt]);

  const saveAnswer = async (value) => {
    if (!currentQuestion) return;

    const payload = { [currentQuestion.key]: value };
    const nextAnswers = { ...answers, [currentQuestion.key]: value };
    const willComplete = QUESTIONS.every((question) => isAnswered(question, nextAnswers));
    if (willComplete) {
      payload.completed = true;
    }

    setSaving(true);
    try {
      const response = await axios.put('/api/customer/onboarding', payload);
      setProfile((previous) => ({
        ...(previous || {}),
        onboarding_state: response.data.onboarding_state,
        onboarding_completed: response.data.onboarding_completed,
        full_name: response.data.full_name,
        cn_address: response.data.cn_address,
        tr_address: response.data.tr_address,
      }));

      await refreshUser();

      if (response.data.onboarding_completed) {
        toast.success('Setup complete');
        window.setTimeout(() => navigate('/home', { replace: true }), 900);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save your answer');
    } finally {
      setSaving(false);
    }
  };

  const handleTextSubmit = (event) => {
    event.preventDefault();
    const value = draftInput.trim();
    if (!value) return;
    saveAnswer(value);
  };

  const toggleSelection = (value) => {
    setDraftSelections((previous) => (
      previous.includes(value)
        ? previous.filter((item) => item !== value)
        : [...previous, value]
    ));
  };

  const handleMultiSubmit = () => {
    if (!draftSelections.length) return toast.error('Select at least one option');
    saveAnswer(draftSelections);
  };

  if (loading || !profile) {
    return <div className="loading-center"><div className="spinner spinner-dark" /></div>;
  }

  return (
    <div className="onboarding-page">
      <header className="page-header onboarding-header">
        <div className="onboarding-brand">
          <span className="onboarding-brand-mark"><MessageCircle size={17} /></span>
          <div>
            <p className="onboarding-kicker">D2D Setup Assistant</p>
            <h1>Smaiz</h1>
            <p className="onboarding-powered">Powered by Smaiz Innovations</p>
          </div>
        </div>
        <div className="onboarding-header-actions">
          <span className="onboarding-secure"><ShieldCheck size={14} /> Secure setup</span>
          <button onClick={() => { logout(); navigate('/'); }} className="btn btn-outline btn-sm" type="button">Sign Out</button>
        </div>
      </header>

      <div className="onboarding-layout">
        <aside className="onboarding-brief">
          <div className="onboarding-brief-orb"><Sparkles size={22} /></div>
          <p className="onboarding-brief-kicker">Your shipping account</p>
          <h2>Everything you need to ship from China and Turkey.</h2>
          <p className="onboarding-brief-copy">Answer a few simple questions and we’ll prepare your personal shipping workspace.</p>
          <div className="onboarding-route-list"><span><i />China → Ghana</span><span><i />Turkey → Ghana</span></div>
          <div className="onboarding-brief-footer"><ShieldCheck size={15} /><span>Your details are kept private and used only to prepare your account.</span></div>
        </aside>

        <main className="onboarding-conversation">
          <div className="onboarding-conversation-header">
            <div className="onboarding-agent-avatar"><MessageCircle size={19} /></div>
            <div><span className="onboarding-agent-label">D2D assistant</span><strong>Smaiz is ready to help</strong></div>
            <div className="onboarding-step-count"><strong>{Math.min(QUESTIONS.length, Math.max(1, currentQuestionIndex + 1))}</strong><span>/ {QUESTIONS.length}</span></div>
          </div>

          <div className="chat-scroll onboarding-scroll">
        {messages.map((message, index) => {
          if (message.role === 'assistant-card') {
            return (
              <div key={`card-${index}`} className="onboarding-address-card">
                <p className="onboarding-address-title">Your shipping mark is live</p>
                <p className="onboarding-address-mark">{profile.shipping_mark}</p>
                <div className="onboarding-address-grid">
                  <div className="onboarding-address-block">
                    <p className="onboarding-address-label">China warehouse</p>
                    <pre>{profile.cn_address}</pre>
                  </div>
                  <div className="onboarding-address-block">
                    <p className="onboarding-address-label">Turkey warehouse</p>
                    <pre>{profile.tr_address}</pre>
                  </div>
                </div>
              </div>
            );
          }

          const bubbleClass = message.role === 'user' ? 'mine' : 'theirs';
          return (
            <div key={`${message.role}-${index}`} style={{ display: 'flex', flexDirection: 'column', alignItems: bubbleClass === 'mine' ? 'flex-end' : 'flex-start' }}>
              <div className={`onboarding-message ${bubbleClass === 'mine' ? 'onboarding-message--user' : 'onboarding-message--assistant'}`}>
                {bubbleClass !== 'mine' && <span className="onboarding-message-avatar"><MessageCircle size={13} /></span>}
                <div className={`chat-bubble ${bubbleClass}`}>
                {message.text || (message.typing ? '...' : '')}
                {message.typing && <span className="typing-cursor">|</span>}
                </div>
              </div>
            </div>
          );
        })}

        {currentQuestion && typedPrompt.length >= currentQuestion.prompt.length && currentQuestion.type !== 'text' && currentQuestion.type !== 'textarea' && (
          <div className="onboarding-options">
            {currentQuestion.options.map((option) => {
              const selected = currentQuestion.type === 'multi'
                ? draftSelections.includes(option.value)
                : answers[currentQuestion.key] === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`onboarding-option${selected ? ' selected' : ''}`}
                  onClick={() => {
                    if (currentQuestion.type === 'single') {
                      saveAnswer(option.value);
                    } else {
                      toggleSelection(option.value);
                    }
                  }}
                  disabled={saving}
                >
                  {option.label}
                </button>
              );
            })}

            {currentQuestion.type === 'multi' && (
              <button
                type="button"
                className="btn btn-primary btn-full"
                onClick={handleMultiSubmit}
                disabled={saving || !draftSelections.length}
              >
                {saving ? <span className="spinner" /> : 'Save and Continue'}
              </button>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {currentQuestion && typedPrompt.length >= currentQuestion.prompt.length && (currentQuestion.type === 'text' || currentQuestion.type === 'textarea') && (
        <form className="chat-composer onboarding-composer" onSubmit={handleTextSubmit}>
          <span className="onboarding-composer-icon"><MessageCircle size={16} /></span>
          {currentQuestion.type === 'textarea' ? (
            <textarea
              className="input-field onboarding-textarea"
              placeholder={currentQuestion.placeholder}
              value={draftInput}
              onChange={(event) => setDraftInput(event.target.value)}
              rows={2}
            />
          ) : (
            <input
              className="input-field"
              placeholder={currentQuestion.placeholder}
              value={draftInput}
              onChange={(event) => setDraftInput(event.target.value)}
            />
          )}
          <button className="btn btn-primary onboarding-send" type="submit" disabled={saving || !draftInput.trim()}>
            {saving ? <span className="spinner" /> : <><span>Send</span><Check size={15} /></>}
          </button>
        </form>
      )}
        </main>
      </div>
    </div>
  );
}
